import type { ReactNode } from "react";
import { IconLanguage, IconLinkOff, IconRefresh, IconRoute } from "@tabler/icons-react";
import { useI18n } from "../i18n";

export function TopBar() {
  const { t, lang, setLang } = useI18n();
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between bg-primary px-4 pt-[env(safe-area-inset-top)] text-white shadow-sm">
      <div className="flex min-w-0 items-center gap-3">
        <img src="/logo.png" alt={t("appName")} className="h-7 w-auto" width={112} height={28} />
        <span className="hidden truncate text-sm opacity-90 sm:inline">{t("liveTracking")}</span>
      </div>
      <button
        type="button"
        onClick={() => setLang(lang === "en" ? "sw" : "en")}
        className="inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-full px-3 text-sm font-semibold transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        lang={lang === "en" ? "sw" : "en"}
      >
        <IconLanguage size={18} aria-hidden="true" />
        {t("switchLanguage")}
      </button>
    </header>
  );
}

export function FooterNote() {
  const { t } = useI18n();
  return (
    <p className="px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-2 text-center font-lemon text-xs text-neutral-500">
      {t("poweredBy")}
    </p>
  );
}

export function StateScreen({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <main className="flex min-h-[calc(100dvh-3.5rem)] flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </div>
      <h1 className="text-xl font-bold text-ink">{title}</h1>
      <p className="mt-2 max-w-sm text-sm leading-relaxed text-neutral-600">{body}</p>
      {action && <div className="mt-6">{action}</div>}
    </main>
  );
}

export function NotFoundScreen() {
  const { t } = useI18n();
  return (
    <StateScreen
      icon={<IconLinkOff size={28} aria-hidden="true" />}
      title={t("notFoundTitle")}
      body={t("notFoundBody")}
    />
  );
}

export function ErrorScreen({ onRetry }: { onRetry: () => void }) {
  const { t } = useI18n();
  return (
    <StateScreen
      icon={<IconRefresh size={28} aria-hidden="true" />}
      title={t("errorTitle")}
      body={t("errorBody")}
      action={
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-ink px-6 text-sm font-semibold text-white transition-colors hover:bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <IconRefresh size={18} aria-hidden="true" />
          {t("retry")}
        </button>
      }
    />
  );
}

export function LandingScreen() {
  const { t } = useI18n();
  return (
    <StateScreen
      icon={<IconRoute size={28} aria-hidden="true" />}
      title={t("landingTitle")}
      body={t("landingBody")}
    />
  );
}

export function LoadingSkeleton() {
  return (
    <main className="lg:grid lg:h-[calc(100dvh-3.5rem)] lg:grid-cols-[minmax(360px,420px)_1fr]" aria-busy="true">
      <div className="h-[42dvh] min-h-[260px] animate-pulse bg-neutral-200 lg:order-2 lg:h-full" />
      <div className="relative -mt-4 space-y-3 px-4 lg:order-1 lg:mt-0 lg:py-4">
        {[128, 176, 144].map((h) => (
          <div key={h} className="animate-pulse rounded-2xl bg-white ring-1 ring-black/5" style={{ height: h }} />
        ))}
      </div>
    </main>
  );
}
